import React from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Space, Table } from "antd";
import { useTranslation } from "react-i18next";

import {
  type ExerciseInWorkout,
  type ExerciseSet as IExerciseSet,
} from "@fitness-app/data-models/entities/TrainingProgram";

import {
  createExerciseInfo,
  formatDuration,
} from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/components/ExerciseInProgramItem/helpers";
import { RestRow } from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/components/ExerciseInProgramItem/RestRow";
import {
  ClientSeesValue,
  useClientHintPreview,
} from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/components/ExerciseInProgramItem/useClientHintPreview";

type SetRow = IExerciseSet & { key: string; setNumber: number; isRest?: false };
type RestRowData = { key: string; isRest: true; restTime: number };
type Row = SetRow | RestRowData;

const COLUMNS = 4;
/** A rest row spans the whole table: the first column takes every cell, the others render nothing. */
const restCell = (row: RestRowData, first = false) => ({
  children: first ? <RestRow seconds={row.restTime} kind="series" /> : null,
  props: { colSpan: first ? COLUMNS : 0 },
});

interface ExerciseSetProps {
  data: ExerciseInWorkout & { orderKey: number; isSuperSet?: false };
  addSeriesToExercise: () => void;
  removeSeriesFromExercise: (setNumber: number) => void;
  updateExerciseInWorkout: (exercise: ExerciseInWorkout) => void;
}

const ExerciseSet = ({
  data,
  addSeriesToExercise,
  removeSeriesFromExercise,
  updateExerciseInWorkout,
}: ExerciseSetProps) => {
  const { t } = useTranslation("workouts");
  const hintPreview = useClientHintPreview();

  const setArray: SetRow[] = Object.keys(data.set).map((key) => ({
    ...data.set[key],
    repeats: data.set[key]?.repeats ?? 0,
    weight: data.set[key]?.weight ?? 0,
    key,
    setNumber: Number(key),
  }));
  // The same rest the live workout puts between series (restTime, nothing after the last one).
  const rows: Row[] = setArray.flatMap((set, index) =>
    index < setArray.length - 1
      ? [set, { key: `rest-${set.key}`, isRest: true as const, restTime: data.restTime ?? 0 }]
      : [set],
  );

  return (
    <div className="flex flex-col gap-4 py-4">
      <Table<Row>
        dataSource={rows}
        rowKey="key"
        pagination={false}
        size="small"
        rowClassName={(row) => (row.isRest ? "restBetweenSeries" : "")}
      >
        <Table.Column<Row>
          title="Seria"
          dataIndex="setNumber"
          key="setNumber"
          render={(setNumber: number, row) => (row.isRest ? restCell(row, true) : setNumber)}
        />
        <Table.Column<Row>
          title="Powtórzenia"
          dataIndex="repeats"
          key="repeats"
          render={(repeats: IExerciseSet["repeats"], record) =>
            record.isRest ? (
              restCell(record)
            ) : (
              <div>
                <div>{createExerciseInfo(repeats)}</div>
                <ClientSeesValue
                  planned={repeats}
                  clientSees={hintPreview?.getSetAsClientSeesIt(data, record.setNumber)?.repeats}
                  weightHint={hintPreview?.weightHint}
                  format={(value) => createExerciseInfo(value as IExerciseSet["repeats"])}
                />
              </div>
            )
          }
        />
        <Table.Column<Row>
          title="Obciąż./Czas trwania"
          dataIndex="weight"
          key="weight"
          render={(weight: IExerciseSet["weight"], record) =>
            record.isRest ? (
              restCell(record)
            ) : (
              <div>
                <div className="flex gap-x-2">
                  <div>{createExerciseInfo(weight, " kg")}</div>
                  {record.duration?.value && (
                    <>
                      <span className="text-gray-700">|</span>
                      <div>
                        {createExerciseInfo(
                          formatDuration(record.duration),
                          ` ${t(`duration.${record.duration.format}`)}`,
                        )}
                      </div>
                    </>
                  )}
                </div>
                <ClientSeesValue
                  planned={weight}
                  clientSees={hintPreview?.getSetAsClientSeesIt(data, record.setNumber)?.weight}
                  weightHint={hintPreview?.weightHint}
                  format={(value) => createExerciseInfo(value as IExerciseSet["weight"], " kg")}
                />
              </div>
            )
          }
        />
        <Table.Column<Row>
          title="Opcje"
          dataIndex="action"
          key="action"
          render={(_, row) =>
            row.isRest ? (
              restCell(row)
            ) : (
              <Space>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  type="text"
                  onClick={() => updateExerciseInWorkout(data)}
                />
                {setArray.length > 1 && (
                  <Button
                    size="small"
                    icon={<DeleteOutlined className="text-red-500" />}
                    danger
                    type="text"
                    onClick={() => removeSeriesFromExercise(row.setNumber)}
                  />
                )}
              </Space>
            )
          }
        />
      </Table>

      <div>
        <Button size="small" icon={<PlusOutlined />} type="dashed" onClick={addSeriesToExercise}>
          {t("exerciseInProgram.addSet")}
        </Button>
      </div>
    </div>
  );
};

export default ExerciseSet;
